/**
 * Roof Cover filter options (Jonathan's request of 2026-09-15).
 *
 * Counties record the same roof under different names ("Asphalt" in one,
 * "Composition Shingle" in the next), so a member who picked one name often
 * got no results: Houston has no "Asphalt" roofs, only "Composition Shingle".
 * Each option now covers every stored value for its material, and the search
 * matches a property whose roof cover is ANY of them.
 *
 * `values` are the exact building_roofCover strings in OpenSearch (a keyword
 * field, so spelling and case must match). The checkbox sends them and the
 * info tooltip lists them, so what a member reads is what the search runs.
 *
 * Two values are here although the request did not name them:
 * "Shingle (Not Wood)" and "Gravel/Rock". The June consolidation had already
 * folded them into the Composition Shingle and Tar & Gravel checkboxes, so
 * leaving them out would make Shingle and Other find fewer properties than
 * the checkboxes they replace.
 *
 * Shake and Contain Solar Panels are renames of Wood and Solar. Their values
 * are unchanged, and `formerly` keeps the old name in the tooltip.
 */
export const ROOF_COVER_OPTIONS = [
  {
    label: "Shingle",
    values: ["Asbestos", "Asphalt", "Composition Shingle", "Shingle (Not Wood)"],
  },
  {
    label: "Other",
    values: [
      "Bermuda",
      "Built-Up",
      "Fiberglass",
      "Gypsum",
      "Masonite/Cement Shake",
      "Tar & Gravel",
      "Gravel/Rock",
      "Other",
    ],
  },
  { label: "Shake", formerly: "Wood", values: ["Wood", "Wood Shake/Shingles"] },
  { label: "Contain Solar Panels", formerly: "Solar", values: ["Solar"] },
  {
    label: "Metal",
    values: ["Metal", "Aluminum", "Copper", "Slate", "Steel", "Tin", "Urethane"],
  },
  {
    label: "Tile",
    values: ["Tile", "Ceramic Tile", "Clay Tile", "Concrete", "Concrete Tile"],
  },
  { label: "Roll Composition", values: ["Roll Composition"] },
];

/**
 * The stored roof cover values a filter currently holds, from any of the
 * shapes it arrives in: a URL or saved-search string ("Asphalt,Metal"), or the
 * form's array, whose entries can themselves be comma-joined. Blanks are
 * dropped so an empty entry can never match a property with no roof cover.
 *
 * @param {string|string[]|null|undefined} raw
 * @returns {string[]}
 */
export function parseRoofCoverValues(raw) {
  if (!raw) return [];
  const entries = Array.isArray(raw) ? raw : [raw];
  const values = entries
    .flatMap((entry) => String(entry ?? "").split(","))
    .map((value) => value.trim())
    .filter((value) => value !== "" && value !== "null" && value !== "undefined");
  return [...new Set(values)];
}

/**
 * "checked" when every value of the option is selected, "partial" when only
 * some are, "unchecked" otherwise. Partial comes from searches saved while
 * the materials were separate checkboxes, e.g. one with only "Asphalt".
 */
export function roofCoverOptionState(option, selectedValues) {
  const selected = option.values.filter((value) => selectedValues.includes(value));
  if (selected.length === 0) return "unchecked";
  return selected.length === option.values.length ? "checked" : "partial";
}

/**
 * The selection after a member clicks an option. A checked option is cleared;
 * an unchecked or partial one becomes fully selected. Values belonging to the
 * other options are left alone.
 */
export function toggleRoofCoverOption(selectedValues, option) {
  if (roofCoverOptionState(option, selectedValues) === "checked") {
    return selectedValues.filter((value) => !option.values.includes(value));
  }
  return [...new Set([...selectedValues, ...option.values])];
}

/**
 * What the filter applies that no fully ticked checkbox shows: options that
 * are only partly selected, and values that belong to no option. The search
 * still uses them exactly as saved, so the form has to say so.
 */
export function roofCoverCarryOver(selectedValues) {
  const partial = ROOF_COVER_OPTIONS.filter(
    (option) => roofCoverOptionState(option, selectedValues) === "partial"
  ).map((option) => ({
    label: option.label,
    values: option.values.filter((value) => selectedValues.includes(value)),
  }));
  const known = new Set(ROOF_COVER_OPTIONS.flatMap((option) => option.values));
  const unknown = selectedValues.filter((value) => !known.has(value));
  return { partial, unknown };
}
